import * as React from "react";

import { Label } from "@/components/ui/label";
import { FieldControlIdContext } from "@/components/ui/field-context";

/**
 * Label + control, properly associated.
 *
 * Four byte-identical copies of this used to live in Employees, Settings,
 * Profile and SiteForm, and none of them linked the `<Label>` to its input:
 * clicking a label didn't focus the field, and screen readers announced an
 * unnamed control (WCAG 1.3.1 / 4.1.2). It also left the browser with nothing
 * to tell one text input from another, which is how picking an autofill
 * suggestion inside a modal came to write into the search box behind it
 * (b284ef8 — mitigated there by defaulting `autoComplete="off"`; this is the
 * underlying fix).
 *
 * Association works two ways, because the two kinds of control differ:
 *  - **DOM controls** (`Input`, `Textarea`) receive the id by cloning — it
 *    lands on the real element.
 *  - **`Select`** cannot: Radix's `Select` root renders no DOM, so a cloned id
 *    is silently dropped (verified, not assumed). Instead `SelectTrigger`
 *    reads the id from context and puts it on the button it renders.
 *
 * Either way the call site stays `<Field label="…"><Input …/></Field>`. A
 * control that already has an `id` keeps it, and `Field` points the label there.
 */
export function Field({
  label,
  hint,
  children,
  className = "space-y-1.5",
}: {
  label: string;
  /**
   * Optional explanatory text under the control. Linked with
   * `aria-describedby` rather than left as loose prose, so a screen reader
   * announces it with the field instead of stranding it as unrelated text.
   */
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const generatedId = React.useId();
  const hintId = `${generatedId}-hint`;
  const child = children as React.ReactElement<{ id?: string }> | React.ReactNode;

  // Respect an id the caller set explicitly; otherwise use ours.
  const explicitId =
    React.isValidElement<{ id?: string }>(child) ? child.props.id : undefined;
  const id = explicitId ?? generatedId;

  const control = React.isValidElement<{ id?: string; "aria-describedby"?: string }>(child)
    ? React.cloneElement(child, {
        ...(explicitId === undefined ? { id } : {}),
        // Keep any describedby the caller already set — appending rather than
        // replacing, since a control may point at an error message too.
        ...(hint
          ? {
              "aria-describedby": [child.props["aria-describedby"], hintId]
                .filter(Boolean)
                .join(" "),
            }
          : {}),
      })
    : children;

  return (
    <FieldControlIdContext.Provider value={id}>
      <div className={className}>
        <Label htmlFor={id} className="text-xs">
          {label}
        </Label>
        {control}
        {hint && (
          <p id={hintId} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
      </div>
    </FieldControlIdContext.Provider>
  );
}
